import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { setValidationState } from "reducers/main";
import Card from "components/Card/Card";
import {
  useGetClaimByIdQuery,
  useGetClaimValidationsQuery,
  usePatchClaimByIdMutation,
} from "services/claims-api";
import { useDispatch, useSelector } from "utils/redux";

import { useForm, Controller } from "react-hook-form";
import { ControlledTextArea } from "components/inputs";
import ClaimsPageContainer from "./ClaimsPageContainer";
import { setCurrentClaim } from "services/claimsDataSlice.ts";
import { Checkbox } from "components/inputs";
import ClaimCMPItemList from "./ClaimCMPItemList";

const SafeHarbor = ({ control, focusedField, handleSave }) => {
  return (
    <Card heading="Safe Harbor" cardNumber={5}>
      <Controller
        key={"positive_declination_received"}
        name={"positive_declination_received"}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Positive declination received"
            name={name}
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("positive_declination_received", e.target.checked);
            }}
          />
        )}
      />
      <Controller
        key={"three_requests_made_to_claimant"}
        name={"three_requests_made_to_claimant"}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Three attempts made to collect query data"
            name={name}
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("three_requests_made_to_claimant", e.target.checked);
            }}
          />
        )}
      />
    </Card>
  );
};

const ClaimCMP = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const dispatch = useDispatch();
  const [patchClaimById] = usePatchClaimByIdMutation();

  const {
    control,
    reset,
    formState: { errors },
  } = useForm();

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
      reset(claim, { keepDirtyValues: true });
    }
  }, [dispatch, reset, claim, isLoading]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchClaimById({
          id: claimId,
          data: field === "" ? value : { [field]: value },
          useDetail: true,
        }).unwrap();
        // .then((result) => {
        //   dispatch(setCurrentClaim(result));
        // });
      } catch (error) {
        console.error("Save failed:", error);
      }
    },
    [patchClaimById, claimId],
  );

  const focusedField = useSelector((state) => state.main.focusedField);

  return (
    <ClaimsPageContainer>
      <div key={"claimant-form-cmp"}>
        <div className="grid-layout gl-1 no-gap-tb">
          <div>
            <ClaimCMPItemList claim={claim} />
          </div>
        </div>
        <div className="grid-layout gl-2 no-gap-tb">
          <div>
            <Card heading="Mitigation Information" cardNumber={1}>
              <ControlledTextArea
                key={`mitigation-notes-${claimId}`}
                name="mitigation_notes"
                control={control}
                errors={errors}
                label="Notes"
                advancedEdit={false}
                onSave={(value) => handleSave("mitigation_notes", value)}
              />
            </Card>
          </div>
          <div>
            <SafeHarbor
              control={control}
              focusedField={focusedField}
              handleSave={handleSave}
            />
          </div>
        </div>
        {/* <div className="grid-layout gl-2 no-gap-tb">
          <SafeHarbor
            control={control}
            focusedField={focusedField}
            handleSave={handleSave}
          />
        </div> */}
      </div>
    </ClaimsPageContainer>
  );
};

export default ClaimCMP;
